import React, { useEffect, useRef, useState } from 'react';

// Speak instead of type — for resolution notes written one-handed in a
// server room or a classroom.
//
// Uses the browser's built-in speech recognition, so there is no dependency and
// nothing to install. Worth knowing: in Chrome the audio is sent to Google's
// speech service to be transcribed, so this should not be used to dictate
// anything confidential. The button only appears where the browser supports it.
const Recognition = typeof window !== 'undefined'
  && (window.SpeechRecognition || window.webkitSpeechRecognition);

export const dictationSupported = () => Boolean(Recognition);

export default function DictateButton({ onText, lang = 'en-IN', title = 'Dictate' }) {
  const rec = useRef(null);
  const [on, setOn] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => () => { try { rec.current && rec.current.stop(); } catch (e) {} }, []);

  if (!Recognition) return null;

  const start = () => {
    try {
      const r = new Recognition();
      rec.current = r;
      r.lang = lang;
      r.continuous = true;
      r.interimResults = false;      // only hand over settled text
      r.onresult = e => {
        let said = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) said += e.results[i][0].transcript;
        }
        said = said.trim();
        if (said) onText(said);
      };
      r.onerror = e => {
        setOn(false);
        setNote(e.error === 'not-allowed'
          ? 'Microphone permission refused'
          : e.error === 'no-speech' ? 'Nothing heard' : 'Dictation stopped');
        setTimeout(() => setNote(''), 3000);
      };
      r.onend = () => setOn(false);
      r.start();
      setOn(true); setNote('');
    } catch (e) { setNote('Could not start dictation'); }
  };

  const stop = () => { try { rec.current && rec.current.stop(); } catch (e) {} setOn(false); };

  return (
    <span className="flex-gap" style={{ gap:6, alignItems:'center' }}>
      <button type="button" className={`btn btn-sm ${on ? 'btn-danger' : 'btn-secondary'}`}
        onClick={on ? stop : start} title={on ? 'Stop dictating' : title}>
        {on ? '⏹ Stop' : '🎤 Speak'}
      </button>
      {on && <span style={{ fontSize:11.5, color:'var(--red)' }}>Listening…</span>}
      {note && <span style={{ fontSize:11.5, color:'var(--text2)' }}>{note}</span>}
    </span>
  );
}
